import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Rocket, X } from "lucide-react";
import { fetchOnboardingState } from "../lib/api";
import { useAuth } from "../lib/auth";

const DISMISS_KEY = "onboarding_banner_dismissed_at";

/**
 * Dashboard banner — Feb 2026.
 *
 * Shows only to admins whose tenant hasn't finished onboarding yet.
 * Dismissible for 24 hours so busy mornings aren't ruined by it.
 */
export default function OnboardingBanner() {
  const { user, isAdmin } = useAuth();
  const [state, setState] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    const t = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return t && Date.now() - t < 24 * 60 * 60 * 1000;
  });

  useEffect(() => {
    // Wait for AuthProvider to attach the axios interceptor before
    // firing the state fetch — otherwise cold loads 401 silently.
    if (!user || !isAdmin) return;
    fetchOnboardingState().then(setState).catch(() => {});
  }, [user, isAdmin]);

  if (!isAdmin || dismissed) return null;
  if (!state || state.is_complete) return null;

  return (
    <div
      className="rounded-lg border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-900/50 p-4 flex items-center gap-3"
      data-testid="onboarding-banner"
    >
      <div className="h-10 w-10 rounded-md bg-emerald-600 text-white flex items-center justify-center shrink-0">
        <Rocket className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm text-slate-900 dark:text-slate-100">
          Finish setting up Tow-Hook
        </div>
        <div className="text-xs text-slate-600 dark:text-slate-400">
          Takes about 2 minutes — business details, depot address and your
          first truck.
        </div>
      </div>
      <Link
        to="/onboarding"
        className="text-sm font-semibold px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white"
        data-testid="onboarding-banner-cta"
      >
        Continue
      </Link>
      <button
        onClick={() => {
          localStorage.setItem(DISMISS_KEY, String(Date.now()));
          setDismissed(true);
        }}
        className="p-1.5 rounded-md hover:bg-emerald-100 dark:hover:bg-emerald-900/40 text-slate-500"
        aria-label="Dismiss for 24 hours"
        data-testid="onboarding-banner-dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
